// Today: the few things that matter most right now, most serious first, each
// with one button. "Not today" and "Not this week" hide one for you alone.
// What's waiting in Tasks shows here too, so Home never says "All clear" while
// the Tasks badge says otherwise.
import { CheckCircle, Clock, Lightning, ListChecks, TrendDown } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api } from "../lib/api.ts";
import { nextQuery, nextSummary } from "../lib/next.ts";
import { Button, buttonClass, Section } from "./ui.tsx";

interface Item {
	key: string;
	tone: "urgent" | "soon" | "watch";
	title: string;
	detail: string;
	action: { label: string; href: string };
}
export const todayQuery = { queryKey: ["today"], queryFn: () => api<{ items: Item[]; more: number }>("/today") };

const LOOK = {
	urgent: { Icon: Lightning, border: "border-stop", icon: "text-stop", word: "Urgent" },
	soon: { Icon: Clock, border: "border-purple", icon: "text-purple", word: "This week" },
	watch: { Icon: TrendDown, border: "border-field", icon: "text-ink-soft", word: "Worth a call" },
} as const;

export function Today({ withTasks }: { withTasks: boolean }) {
	const queryClient = useQueryClient();
	const q = useQuery(todayQuery);
	const next = useQuery({ ...nextQuery, enabled: withTasks });
	const waiting = withTasks ? nextSummary(next.data?.items ?? []) : null;
	const snooze = useMutation({
		mutationFn: (v: { item: string; days: 1 | 7 }) => api("/today/snooze", { body: v }),
		onMutate: (v) =>
			queryClient.setQueryData<{ items: Item[]; more: number }>(["today"], (old) =>
				old ? { ...old, items: old.items.filter((i) => i.key !== v.item) } : old,
			),
		onSettled: () => queryClient.invalidateQueries({ queryKey: ["today"] }),
	});
	// Wait for both, so "All clear" never shows for a moment before what's waiting arrives.
	if (!q.data || (withTasks && !next.data && !next.isError)) return null;
	const urgent = q.data.items.length > 0;
	// A card with its heading inside, like Your tasks beside it. The most serious item
	// gets the page's main button; the rest are secondary, so one next step stands out.
	return (
		<Section title="Today" id="today-h" className="mb-6">
			{!urgent && !waiting ? (
				<p className="flex items-center gap-2 font-semibold">
					<CheckCircle size={26} weight="fill" aria-hidden="true" className="shrink-0 text-clear" />
					All clear. No shifts at risk, nobody finishing without work, no clients slipping.
				</p>
			) : !urgent ? (
				<p>Nothing urgent: no shifts at risk, nobody finishing without work, no clients slipping.</p>
			) : (
				<ul className="mt-1 space-y-3">
					{q.data.items.map((i, n) => {
						const look = LOOK[i.tone];
						return (
							<li
								key={i.key}
								className={`flex flex-col gap-3 border-l-4 py-1 pl-3 sm:flex-row sm:items-center sm:justify-between ${look.border}`}
							>
								<div className="flex min-w-0 gap-3">
									<look.Icon size={24} weight="fill" aria-hidden="true" className={`mt-0.5 shrink-0 ${look.icon}`} />
									<div className="min-w-0">
										<p className="font-semibold">
											<span className="sr-only">{look.word}: </span>
											{i.title}
										</p>
										<p className="text-sm text-ink-soft">{i.detail}</p>
									</div>
								</div>
								<div className="flex shrink-0 flex-wrap items-center gap-x-1 gap-y-1 sm:justify-end">
									<Link to={i.action.href} className={buttonClass(n === 0 ? "primary" : "secondary")}>
										{i.action.label}
									</Link>
									<Button variant="quiet" onClick={() => snooze.mutate({ item: i.key, days: 1 })}>
										Not today
									</Button>
								</div>
							</li>
						);
					})}
				</ul>
			)}
			{q.data.more ? <p className="mt-2 text-sm text-ink-soft">And {q.data.more} more after these.</p> : null}
			{waiting ? (
				<div className="mt-4 flex flex-col gap-3 border-l-4 border-purple py-1 pl-3 sm:flex-row sm:items-center sm:justify-between">
					<p className="flex min-w-0 gap-3 font-semibold">
						<ListChecks size={24} weight="fill" aria-hidden="true" className="mt-0.5 shrink-0 text-purple" />
						<span>Waiting in Tasks: {waiting}.</span>
					</p>
					{/* The main button only when nothing above is urgent. */}
					<Link to="/tasks" className={`${buttonClass(urgent ? "secondary" : "primary")} shrink-0`}>
						See what's waiting
					</Link>
				</div>
			) : null}
		</Section>
	);
}
